import { DEFAULT_INTELLIGENCE_WINDOW } from '@conversed/shared/src/central/constants/intelligence';
import { Sentiment } from '@conversed/shared/src/central/constants/sentiment';
import { AIPerformanceScoresSchema } from '@conversed/shared/src/central/types/ai-performance';
import { StudioType } from '@conversed/shared/src/central/types/studio.type';
import { TimeWindow } from '@conversed/shared/src/central/types/time-window.type';
import { z } from 'zod';
import { AccuracyTestSchema } from '../dashboard/dashboard.schema';
import { DEFAULT_STUDIO_PERFORMANCE_WINDOW } from './home.default';

// AI Performance chart inputs - reused on Home v2 so its chart shows the same data
// as the Dashboard (subjective/objective series gated by the same settings).
export const AIChartSettingsSchema = z.object({
  showSubjective: z.boolean(),
  showCombined: z.boolean(),
  showObjective: z.boolean(),
});

export const AIAccuracyBenchmarkSchema = z.object({
  enabled: z.boolean(),
  value: z.number(),
});

export const HomeDataSchema = z.object({
  botName: z.string(),
  totalQuestions: z.number(),
  aiAccuracy: z.number(),
  activeUsers: z.number(),
});

// Time window for the Intelligence section. Default 7d.
export const TimeWindowSchema = z.enum(TimeWindow);

export const IntelligenceQuerySchema = z.object({
  window: TimeWindowSchema.default(DEFAULT_INTELLIGENCE_WINDOW),
});

export const SentimentBucketsSchema = z.object({
  unhappy: z.number(),
  neutral: z.number(),
  happy: z.number(),
  total: z.number(),
});

export const IntelligenceTopicSchema = z.object({
  id: z.string(),
  name: z.string(),
  volume: z.number(),
  lastAnalyzedAt: z.string().nullable(),
  // Weighted satisfaction score 0..1 ((positive + 0.5*neutral) / total); null when no
  // classified conversations exist yet. Drives the sentiment bar length and colour.
  sentimentScore: z.number().nullable(),
  sentiment: SentimentBucketsSchema,
});

export const IntelligenceResponseSchema = z.object({
  window: TimeWindowSchema,
  topics: z.array(IntelligenceTopicSchema),
});

export const IntelligenceQuestionsQuerySchema = z.object({
  topicId: z.string(),
  window: TimeWindowSchema.default(DEFAULT_INTELLIGENCE_WINDOW),
});

export const IntelligenceQuestionSchema = z.object({
  _id: z.string(),
  question: z.string(),
  answer: z.string(),
  sentiment: z.enum(Sentiment).nullable(),
});

export const IntelligenceQuestionsResponseSchema = z.object({
  questions: z.array(IntelligenceQuestionSchema),
});

export const WhatsChangedQuerySchema = z.object({
  window: TimeWindowSchema.default(DEFAULT_INTELLIGENCE_WINDOW),
});

// One day bucket of a topic's volume, for the trend sparkline.
export const TopicTrendPointSchema = z.object({
  date: z.string(),
  count: z.number(),
});

export const WhatsChangedTopicSchema = z.object({
  id: z.string(),
  name: z.string(),
  currentVolume: z.number(),
  priorVolume: z.number(),
  currentSentimentScore: z.number().nullable(),
  priorSentimentScore: z.number().nullable(),
  currentSentiment: SentimentBucketsSchema,
  priorSentiment: SentimentBucketsSchema,
  firstSeenAt: z.string().nullable(),
  lastAnalyzedAt: z.string().nullable(),
  trend: z.array(TopicTrendPointSchema),
});

export const WhatsChangedResponseSchema = z.object({
  window: TimeWindowSchema,
  topics: z.array(WhatsChangedTopicSchema),
});

// Snapshot of one studio for the "Other studios" section. `isInstalled` reflects
// whether the company group has that studio activated.
export const StudioSnapshotSchema = z.object({
  type: z.enum(StudioType),
  isInstalled: z.boolean(),
  metric: z.number(),
});

// One voice topic on the studio-performance panel. Same source as the
// Intelligence topic list, projected to just id/name/volume.
export const VoiceTopicSchema = z.object({
  id: z.string(),
  name: z.string(),
  volume: z.number(),
});

// Studio performance metrics included in the overview. Chat, Ticket and Voice carry
// real data; Email fields are placeholders (0) until email tracking is implemented.
export const StudioPerformanceSchema = z.object({
  // Chat: real data
  questionsAnswered: z.number(),
  uniqueUsers: z.number(),
  // CSAT score (0..100) from platform settings. Null when the company does not
  // have the CSAT_CARD package feature enabled.
  csatPercent: z.number().nullable(),
  // Chat: users with exactly one question in the window vs. users with more than
  // one - the "Single questions vs. conversations" card.
  singleQuestions: z.number(),
  groupConversations: z.number(),
  // Chat: whether the company has the Time saved card configured
  // (`timeSavedEnabled` in platform settings). False hides the card.
  timeSavedEnabled: z.boolean(),
  // Ticket: real data
  // Deflected + escalated (conversations that went through the ticket deflection flow).
  responsesGenerated: z.number(),
  // (deflected + noButtonPress) / total * 100. 0 = no data (same sentinel as Dashboard).
  deflectionRate: z.number(),
  // Email
  draftsGenerated: z.number(),
  emailsSent: z.number(),
  uniqueReceivers: z.number(),
  // Voice: real data (voice studios only; 0/null/false/[] for other studios)
  voiceConversationCount: z.number(),
  // Mean call duration. 0 when there are no calls in the window.
  voiceAvgDurationSecs: z.number(),
  voiceTotalDurationSecs: z.number(),
  // Mean LLM response time. Null when no call reported a response time.
  voiceAvgResponseTimeSecs: z.number().nullable(),
  // Whether an ElevenLabs agent is assigned to this company.
  voiceAgentConfigured: z.boolean(),
  // Windowed top voice topics for the panel.
  voiceTopics: z.array(VoiceTopicSchema),
});

export const StudioPerformanceQuerySchema = z.object({
  window: TimeWindowSchema.default(DEFAULT_STUDIO_PERFORMANCE_WINDOW),
});

export const HomeOverviewSchema = z.object({
  botName: z.string(),
  aiAccuracy: z.number(),
  totalQuestions: z.number(),
  activeUsers: z.number(),
  studios: z.array(StudioSnapshotSchema),
  intelligence: IntelligenceResponseSchema,
  whatsChanged: WhatsChangedResponseSchema,
  studioPerformance: StudioPerformanceSchema,
  // Inputs for the A.I. Performance chart - same data the Dashboard chart uses.
  // Bundled here so the whole Home v2 page loads in one request (no fetch-on-expand).
  performanceScores: AIPerformanceScoresSchema,
  accuracyTests: AccuracyTestSchema.array().default([]),
  aiChartSettings: AIChartSettingsSchema,
  aiAccuracyBenchmark: AIAccuracyBenchmarkSchema,
});
